import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Button,
  Checkbox,
  Paper,
  Collapse,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Modal,
} from "@mui/material";
import styled from "styled-components";

import CreateMetadata from './CreateMetadata';
import EditIcon from '../../../assets/icons/Edit_Icon.svg';
import PopoutIcon from '../../../assets/icons/svgs/Popout_Icon.svg';
import CopyIcon from '../../../assets/icons/CopyFileIcon_Black.svg';

import NavigationBar from "../../../components/nav/NavigationBar.tsx";

const Container = styled.div`
  align-items: stretch;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  // gap: 0px;
  justify-content: flex-start;
  padding: clamp(40px, 10vw, 80px) 0 0 0;
  position: relative;
`;

const initialData = [
  {
    name: "Energy Output - Charger ID - Las Vegas Nevada",
    date: "2023-02-15",
    status: "Active",
    type: "Continuous",
    user: "User1",
  },
  {
    name: "Energy Consumption - Station ID - Las Vegas Nevada",
    date: "2023-01-10",
    status: "Pending",
    type: "Archival",
    user: "User2",
  },
  {
    name: "Camera Feeds - BESS ID - Las Vegas Nevada",
    date: "2023-03-20",
    status: "Inactive",
    type: "Continuous",
    user: "User3",
  },
];

function TabSection({ selectedTab, metadata }) {
  if (!metadata) return null;

  const labelStyle = {
    fontWeight: 600,
    minWidth: "200px",
    marginRight: "16px",
    whiteSpace: "nowrap",
  };

  const rowStyle = {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "12px",
  };

  const renderRow = (label, value) => (
    <Box key={label} sx={rowStyle}>
      <Typography sx={labelStyle}>{label}:</Typography>
      <Typography sx={{ flex: 1 }}>{value || "—"}</Typography>
    </Box>
  );

  if (selectedTab === "Overview") {
    return (
      <Box p={2}>
        {renderRow("Description", metadata?.description)}
        {renderRow("Metadata Created", metadata?.date)}
        {renderRow("Last Modified", metadata?.lastModified)}
        {renderRow("Location Category", metadata?.locationCategory)}
        {renderRow("Location Type", metadata?.locationType)}
        {renderRow("Location", metadata?.location)}
        {renderRow("Container", metadata?.containerName)}
        {renderRow("Environment", metadata?.environment)}
        {renderRow("Interactor", metadata?.interactor)}
      </Box>
    );
  }

  if (selectedTab === "Details") {
    return (
      <Box p={2}>
        {renderRow("Description", metadata?.description)}
        {renderRow("Date Created", metadata?.date)}
        {renderRow("Last Modified", metadata?.lastModified)}
        {renderRow("Data Sample", "")}
        <TextField
          fullWidth
          multiline
          rows={3}
          disabled
          variant="outlined"
          value={metadata?.schemaBody || ""}
        />
      </Box>
    );
  }

  if (selectedTab === "Context") {
    const location =
      metadata?.locationCategory === "Custom Region"
        ? metadata?.selectedCoordinates || metadata?.geoNotes || "—"
        : [
            metadata?.streetAddress,
            metadata?.city,
            metadata?.stateProvince,
            metadata?.postalCode,
            metadata?.country,
          ]
            .filter(Boolean)
            .join(", ");

    return (
      <Box p={2}>
        {renderRow("Location Category", metadata?.locationCategory)}
        {renderRow("Location Type", metadata?.locationType)}
        {renderRow("Location", location)}
        {renderRow("Container", metadata?.containerName)}
        {renderRow("Environment", metadata?.environment)}
        {renderRow("Interactor", metadata?.interactor)}
        {renderRow("ISIC Code(s)", "N/A")}
        {renderRow("Legal Type", metadata?.legalType)}
        {renderRow("Regulatory Type", metadata?.regulatoryType)}
      </Box>
    );
  }

  if (selectedTab === "Provenance") {
    return (
      <Box p={2}>
        {renderRow("Source Name", metadata?.sourceName)}
        {renderRow("Source Category", metadata?.sourceCategory)}
        {renderRow("Source Type", metadata?.sourceType)}
        {renderRow("Sensor Type", metadata?.sensorType)}
        {renderRow("Manufacturer", metadata?.manufacturer)}
        {renderRow("Make", metadata?.make)}
        {renderRow("Model", metadata?.model)}
        {renderRow("Version", metadata?.version)}
        {renderRow("Originator(s)", metadata?.selectedOriginators)}
        {renderRow("Owner(s)", metadata?.selectedOwners)}
      </Box>
    );
  }

  if (selectedTab === "Technical") {
    return (
      <Box p={2}>
        {renderRow("Measurement Unit", metadata?.unitOfMeasure)}
        {renderRow("Format", metadata?.fileType)}
        {renderRow("Type", metadata?.type || "Continuous")}
        {renderRow("Show Schema Body", metadata?.schemaBody)}
        {renderRow("Show Citation", metadata?.citation)}
        {renderRow(
          "Show Update Frequency & Ingestion Level",
          `${metadata?.updateInterval || "—"} ${metadata?.updateIntervalUnit || ""}`
        )}
      </Box>
    );
  }

  if (selectedTab === "Feeds") {
    return (
      <Box p={2}>
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <TextField label="Search Feeds" size="small" variant="outlined" />
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Name", "Description", "Date Created"].map((head) => (
                  <TableCell key={head}>{head}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{metadata?.name}</TableCell>
                <TableCell>{metadata?.description}</TableCell>
                <TableCell>{metadata?.date}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return null;
}

function ConfiguredMetadata() {
  const navigate = useNavigate();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [selectedTab, setSelectedTab] = useState("Overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");

  const [editingMetadata, setEditingMetadata] = useState(null);

  const [copiedMetadata, setCopiedMetadata] = useState(null);

  const [metadataList, setMetadataList] = useState(initialData);

  const [openDetails, setOpenDetails] = useState(null);

  const handleAddMetadata = (newMetadata, isEdit = false) => {
    const today = new Date().toISOString().split("T")[0];

    if (isEdit) {
      setMetadataList((prev) =>
        prev.map((item) =>
          item.name === editingMetadata.name
            ? { ...item, ...newMetadata, lastModified: today }
            : item
        )
      );
    } else {
      setMetadataList((prev) => [
        ...prev,
        {
          ...newMetadata,
          date: today,
          lastModified: today,
          status: "Active",
          user: "CurrentUser",
          type: newMetadata.type || "Continuous",
        },
      ]);
    }

    setEditingMetadata(null);
    setShowCreateForm(false);
  };

  const filteredData = useMemo(() => {
    let data = [...metadataList];
    
    switch (sortField) {
      case "originator-asc":
        data.sort((a, b) => a.user?.localeCompare(b.user));
        break;
      case "originator-desc":
        data.sort((a, b) => b.user?.localeCompare(a.user));
        break;
      case "owner-asc":
        data.sort((a, b) => a.user?.localeCompare(b.user));
        break;
      case "owner-desc":
        data.sort((a, b) => b.user?.localeCompare(a.user));
        break;
      case "date-asc":
        data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case "date-desc":
        data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case "status-active-first":
        data.sort((a, b) => a.status.localeCompare(b.status));
        break;
      case "status-inactive-first":
        data.sort((a, b) => b.status.localeCompare(a.status));
        break;
      case "type-archival-first":
        data.sort((a, b) => a.type.localeCompare(b.type));
        break;
      case "type-continuous-first":
        data.sort((a, b) => b.type.localeCompare(a.type));
        break;
      case "user-asc":
        data.sort((a, b) => a.user.localeCompare(b.user));
        break;
      case "user-desc":
        data.sort((a, b) => b.user.localeCompare(a.user));
        break;
      default:
        break;
    }
  
    return data;
  }, [searchTerm, sortField, metadataList]);

  const handleRowClick = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
    setSelectedTab("Overview");
  };

  return (
    <Container>
      <NavigationBar />
      <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
        <h1 style={{ fontSize: "24px", textAlign: "center" }}>
          Configured Metadata
        </h1>
        {/* Controls Section */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flexGrow: 1, minWidth: "300px", maxWidth: "500px" }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Sort</div>
              <TextField
                select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                size="small"
                variant="outlined"
                SelectProps={{ native: true }}
                style={{ width: "250px", backgroundColor: "white" }}
              >
                <option value="" disabled hidden>
                  Select
                </option>
                <option value="originator-asc">Originator A-Z</option>
                <option value="originator-desc">Originator Z-A</option>
                <option value="owner-asc">Owner A-Z</option>
                <option value="owner-desc">Owner Z-A</option>
                <option value="date-desc">Date Created Newest to Oldest</option>
                <option value="date-asc">Date Created Oldest to Newest</option>
                <option value="status-active-first">Status (Active to Inactive)</option>
                <option value="status-inactive-first">Status (Inactive to Active)</option>
                <option value="type-archival-first">Type (Archival to Continuous)</option>
                <option value="type-continuous-first">Type (Continuous to Archival)</option>
                <option value="user-asc">By User (A-Z)</option>
                <option value="user-desc">By User (Z-A)</option>
              </TextField>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: "#0077cc",
                  color: "black",
                  background: "white",
                  height: "40px",
                  minWidth: "180px",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  border: "1px solid gray",
                }}
                onClick={() => setShowCreateForm(true)}
              >
                + Create New
              </Button>

              <Button
                variant="contained"
                style={{
                  backgroundColor: "#0077cc",
                  color: "white",
                  background: "var(--terbine-dark-gray)",
                  height: "40px",
                  minWidth: "180px",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                }}
              >
                + Create New Group
              </Button>
            </div>
          </div>
        </div>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            "& *": {
              fontFamily: "Inter, Oxygen, Roboto, sans-serif !important",
            },
          }}
        >
          <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--terbine-blue)",
                  "& th": {
                    color: "white",
                    fontSize: "clamp(12px, 1.5vw, 18px)",
                    fontWeight: 300,
                    borderBottom: "none",
                  },
                  borderBottom: "none",
                }}
              >
                {["Name", "Date Created", "Status", "Type", "Created By"].map((head, i) => (
                  <TableCell key={i} sx={{ borderBottom: "none" }}>{head}</TableCell>
                ))}
                <TableCell sx={{ borderBottom: "none" }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.map((row, index) => (
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    onClick={() => handleRowClick(index)}
                    sx={{
                      backgroundColor: "#F2F2F2",
                      cursor: "pointer",
                      borderBottom: index !== filteredData.length - 1 ? "3px solid #fff" : "none",
                      borderTop: "none",
                      "& > td": {
                        color: "black",
                        borderBottom: index !== filteredData.length - 1 ? "3px solid #fff" : "none",
                        borderTop: "none",
                      },
                    }}
                  >
                    <TableCell>
                      {row.name.startsWith("[CLONED] ") ? (
                        <span>
                          <strong>[CLONED]</strong> {row.name.replace("[CLONED] ", "")}
                        </span>
                      ) : (
                        row.name
                      )}
                    </TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor:
                            row.status.toLowerCase() === "active"
                              ? "var(--header-green)"
                              : "white",
                          border: "1px solid #ccc",
                          margin: "0 auto",
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.user}</TableCell>
                    <TableCell />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={7} sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: "none" }}>
                      <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
                        <Box p={3} bgcolor="#f9f9f9" borderTop="none" borderRadius="4px" position="relative">
                          <Box
                            sx={{
                              position: "absolute",
                              top: 16,
                              right: 16,
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const clonedRow = {
                                  ...row,
                                  name: `[CLONED] ${row.name}`,
                                  date: new Date().toISOString().split("T")[0],
                                  user: "CurrentUser",
                                };

                                setMetadataList((prev) => {
                                  const originalIndex = prev.findIndex((item) => item === row);
                                  const newList = [...prev];
                                  newList.splice(originalIndex + 1, 0, clonedRow);
                                  return newList;
                                });
                              }}
                            >
                              <img src={CopyIcon} alt="Clone" width={20} height={20} />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingMetadata(row);
                                setShowCreateForm(true);
                              }}
                            >
                              <img src={EditIcon} alt="Edit" width={20} height={20} />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDetails(row);
                              }}
                            >
                              <img src={PopoutIcon} alt="Popout" width={20} height={20} />
                            </IconButton>
                          </Box>

                          <Typography
                            variant="h6"
                            align="center"
                            gutterBottom
                            sx={{ fontWeight: "bold" }}
                          >
                            {row.name}
                          </Typography>

                          <Box border="1px solid #ccc" borderRadius="4px" width="100%">
                            <Tabs
                              value={selectedTab}
                              onChange={(e, newVal) => setSelectedTab(newVal)}
                              textColor="inherit"
                              variant="fullWidth"
                              sx={{
                                borderBottom: "1px solid #ccc",
                                "& .MuiTab-root": {
                                  textTransform: "none",
                                  fontWeight: 500,
                                  padding: "10px 12px",
                                  borderRight: "1px solid #ccc",
                                  color: "#333",
                                  minWidth: 0,
                                },
                                "& .MuiTab-root:last-of-type": {
                                  borderRight: "none",
                                },
                                "& .Mui-selected": {
                                  backgroundColor: "var(--terbine-blue)",
                                  color: "white",
                                },
                              }}
                            >
                              {["Overview", "Details", "Context", "Provenance", "Technical", "Feeds"].map((tab) => (
                                <Tab key={tab} label={tab} value={tab} />
                              ))}
                            </Tabs>

                            <Box sx={{ minHeight: "150px", backgroundColor: "white" }}>
                              <TabSection selectedTab={selectedTab} metadata={row} />
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Modal
          open={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
            setEditingMetadata(null);
          }}
          sx={{ overflow: 'auto' }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '1000px',
              maxHeight: '95%',
              overflowY: 'auto',
              boxShadow: 24,
              borderRadius: 2,
            }}
          >
            <CreateMetadata
              onSubmit={handleAddMetadata}
              initialData={editingMetadata}
              isEdit={Boolean(editingMetadata)}
              startStep={1}
            />
          </Box>
        </Modal>
        <Modal
          open={Boolean(openDetails)}
          onClose={() => setOpenDetails(null)}
          sx={{ overflow: 'auto' }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95%',
              maxWidth: '1200px',
              maxHeight: '95%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              overflowY: 'auto',
            }}
          >
            {openDetails && (
              <>
                <Typography variant="h6" align="center" mb={2}>
                  {openDetails.name}
                </Typography>

                <Divider sx={{ mb: 0.2, borderBottomWidth: "2px", borderColor: "var(--terbine-blue)" }} />
                <Divider sx={{ mb: 3, borderBottomWidth: "2px", borderColor: "var(--terbine-blue)" }} />

                <Box display="flex" gap={4} flexWrap="wrap">
                  {/* Left column */}
                  <Box flex={1} minWidth="300px">
                    {[
                      { label: "Description", value: openDetails.description },
                      { label: "Metadata Created", value: openDetails.date },
                      { label: "Last Modified", value: openDetails.lastModified || "Not available" },
                      { label: "Last Data Update", value: "Not available" },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" mb={1}>
                        <Typography minWidth="180px">{label}:</Typography>
                        <Typography>{value || "—"}</Typography>
                      </Box>
                    ))}

                    <Box mb={2}>
                      <Typography mb={1}>Data Sample:</Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        disabled
                        variant="outlined"
                        value={openDetails?.schemaBody || ""}
                      />
                    </Box>

                    {[
                      { label: "Location Category", value: openDetails.locationCategory },
                      { label: "Location Type", value: openDetails.locationType },
                      {
                        label: "Location",
                        value:
                          openDetails.locationCategory === "Custom Region"
                            ? openDetails.selectedCoordinates || openDetails.geoNotes || "—"
                            : [
                                openDetails.streetAddress,
                                openDetails.city,
                                openDetails.stateProvince,
                                openDetails.postalCode,
                                openDetails.country,
                              ]
                                .filter(Boolean)
                                .join(", "),
                      },
                      { label: "Container", value: openDetails.containerName },
                      { label: "Environment", value: openDetails.environment },
                      { label: "Interactor", value: openDetails.interactor },
                      { label: "ISIC Code(s)", value: "N/A" },
                      { label: "Legal Type", value: openDetails.legalType },
                      { label: "Regulatory Type", value: openDetails.regulatoryType },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" mb={1}>
                        <Typography minWidth="180px">{label}:</Typography>
                        <Typography>{value || "—"}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Right column */}
                  <Box flex={1} minWidth="300px">
                    {[
                      { label: "Source Name", value: openDetails.sourceName },
                      { label: "Source Category", value: openDetails.sourceType },
                      { label: "Source Type", value: openDetails.sourceType },
                      { label: "Sensor Type", value: openDetails.sensorType },
                      { label: "Manufacturer", value: openDetails.manufacturer },
                      { label: "Make", value: openDetails.make },
                      { label: "Model", value: openDetails.model },
                      { label: "Version", value: openDetails.version },
                      { label: "Originator(s)", value: openDetails.selectedOriginators },
                      { label: "Owner(s)", value: openDetails.selectedOwners },
                      { label: "Measurement Unit", value: openDetails.unitOfMeasure },
                      { label: "Format", value: openDetails.fileType },
                      { label: "Type", value: openDetails.type },
                      { label: "GUID", value: "—" },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" mb={1}>
                        <Typography minWidth="180px">{label}:</Typography>
                        <Typography>{value || "—"}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Feeds Table */}
                <Typography variant="subtitle1" mt={4} mb={1}>
                  Feeds
                </Typography>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#F2F2F2",
                          "& th": {
                            borderBottom: "none",
                            fontWeight: 600,
                          },
                        }}
                      >
                        {["Name", "Description", "Date Created"].map((head) => (
                          <TableCell key={head}>{head}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{
                          "& td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell>{openDetails.name}</TableCell>
                        <TableCell>{openDetails.description}</TableCell>
                        <TableCell>{openDetails.date}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Modal>
      </div>
    </Container>
    );
}

export default ConfiguredMetadata;